import { TESTIDS, SCHEDULE_FORM_URL } from "@/lib/site";
import { trackWhatsAppClick } from "@/lib/analytics";
import { useReveal } from "@/lib/useReveal";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_compassionate-carson-7/artifacts/zy4ys4kr_WhatsApp%20Image%202026-06-06%20at%207.12.51%20PM.jpeg";
const LOGO =
  "https://customer-assets.emergentagent.com/job_compassionate-carson-7/artifacts/ol86er6s_ad07528f-7566-40df-b52c-39962b21fedd.jfif";

export default function Hero() {
  const r1 = useReveal();
  const r2 = useReveal(0.1);

  return (
    <section
      id="inicio"
      data-testid={TESTIDS.hero.section}
      className="relative overflow-hidden pt-32 md:pt-36 pb-20 md:pb-28"
    >
      {/* Soft organic background shapes */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-dusty-rose/35 blur-3xl" />
        <div className="absolute top-40 -right-32 w-[460px] h-[460px] rounded-full bg-sage/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[300px] h-[300px] rounded-full bg-lavender/30 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        {/* Text */}
        <div className={`${r1.className} lg:col-span-6 relative`}>
          <div className="inline-flex items-center gap-2.5 mb-7 rounded-full bg-warm-beige/70 border border-dusty-rose/30 pl-2 pr-4 py-1.5 text-[0.78rem] tracking-[0.18em] uppercase text-editorial-gray">
            <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-accent-magenta/10">
              <span className="block w-1.5 h-1.5 rounded-full bg-accent-magenta animate-soft-pulse" />
            </span>
            Psicoterapia · Musicoterapia humanista
          </div>

          <h1
            data-testid={TESTIDS.hero.headline}
            className="font-serif text-[2.4rem] sm:text-5xl lg:text-[3.8rem] text-editorial-dark leading-[1.08] tracking-tight"
          >
            Si pudieras expresar tus{" "}
            <span className="italic text-accent-magenta">emociones</span>
            <br />
            a través del{" "}
            <span className="relative inline-block">
              sonido
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
                aria-hidden
              >
                <path
                  d="M2 7 Q 50 0 100 7 T 198 7"
                  stroke="#BC7F6B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
            <span className="text-editorial-gray">…</span>
            <br />
            <span className="text-editorial-gray font-light italic">¿a qué sonarían?</span>
          </h1>

          <p className="mt-7 max-w-xl text-[1.05rem] md:text-[1.12rem] text-editorial-gray leading-relaxed">
            Soy <strong className="text-editorial-dark font-medium">Berenice Pavón</strong>,
            psicóloga, música profesional y especialista en{" "}
            <em>Musicoterapia Humanista</em> egresada del IMMH. Acompaño a las personas a
            identificar sus emociones, comprender la necesidad detrás de ellas y abrir
            nuevas posibilidades para atravesar los desafíos de la vida desde una mirada
            sensible, humana y profunda.
          </p>

          <div className="mt-9 flex flex-wrap gap-4">
            <a
              data-testid={TESTIDS.hero.ctaSchedule}
              href={SCHEDULE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("hero_cta")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-accent-magenta px-7 py-3.5 text-warm-white text-[0.95rem] hover:-translate-y-0.5 transition-all duration-300 shadow-md shadow-accent-magenta/20 hover:shadow-lg hover:shadow-accent-magenta/30"
            >
              Agendar consulta
              <svg viewBox="0 0 24 24" className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>
            <button
              data-testid={TESTIDS.hero.ctaLearnMore}
              onClick={() =>
                document.getElementById("sobre-mi")?.scrollIntoView({ behavior: "smooth" })
              }
              className="inline-flex items-center gap-2 rounded-full bg-transparent border border-editorial-gray/40 text-editorial-dark px-7 py-3.5 text-[0.95rem] hover:bg-warm-beige/60 hover:border-editorial-gray/70 transition-all"
            >
              Conocer más
            </button>
          </div>

          {/* Sound wave illustration */}
          <div className="mt-12 flex items-start gap-5 text-accent-magenta">
            <div className="flex items-end h-12 text-accent-magenta shrink-0 mt-2">
              {[0.4, 0.8, 0.55, 1, 0.65, 0.9, 0.5, 0.75, 0.4].map((h, i) => (
                <span
                  key={i}
                  className="soundwave-bar"
                  style={{
                    height: `${h * 100}%`,
                    animationDelay: `${i * 0.12}s`,
                  }}
                />
              ))}
            </div>
            <p className="font-serif italic text-[1.35rem] md:text-[1.55rem] lg:text-[1.7rem] text-editorial-dark leading-[1.3] max-w-md">
              Un espacio para escuchar lo que las palabras a veces
              <span className="text-terracotta"> no pueden decir</span>.
            </p>
          </div>
        </div>

        {/* Big editorial portrait */}
        <div className={`${r2.className} lg:col-span-6 relative`}>
          <div className="relative aspect-[4/5] w-full max-w-[560px] mx-auto">
            {/* Main portrait */}
            <div className="absolute inset-0 rounded-[2.2rem] overflow-hidden shadow-2xl shadow-editorial-dark/15 border border-warm-beige">
              <img
                src={PORTRAIT}
                alt="Berenice Pavón — psicóloga y musicoterapeuta humanista"
                className="w-full h-full object-cover"
                loading="eager"
              />
              {/* Subtle warm overlay to harmonize with palette */}
              <div className="absolute inset-0 bg-gradient-to-t from-editorial-dark/30 via-transparent to-transparent" />
            </div>

            {/* Floating brand stamp */}
            <div className="absolute -top-6 -left-6 md:-top-7 md:-left-10 w-32 md:w-36 rounded-2xl bg-warm-white/95 backdrop-blur-sm shadow-xl shadow-editorial-dark/10 border border-dusty-rose/30 p-3 rotate-[-4deg]">
              <img src={LOGO} alt="Musicoterapia Humanista Toluca" className="w-full h-auto object-contain block" />
            </div>

            {/* Floating credential chip */}
            <div className="absolute -bottom-6 left-4 md:left-8 rounded-2xl bg-accent-magenta text-warm-white shadow-xl px-5 py-3.5">
              <p className="text-[0.65rem] tracking-[0.22em] uppercase opacity-80">Cédula profesional</p>
              <p className="font-serif text-xl leading-none mt-1">15510639</p>
            </div>

            {/* Decorative dot grid */}
            <div className="absolute -bottom-6 -right-6 w-24 h-24 opacity-50" aria-hidden>
              <svg viewBox="0 0 100 100" className="w-full h-full">
                {[...Array(6)].map((_, r) =>
                  [...Array(6)].map((_, c) => (
                    <circle
                      key={`${r}-${c}`}
                      cx={6 + c * 16}
                      cy={6 + r * 16}
                      r="1.2"
                      fill="#6A004D"
                    />
                  ))
                )}
              </svg>
            </div>
          </div>

          {/* Italic caption below portrait */}
          <p className="mt-16 max-w-lg mx-auto text-center font-serif italic text-editorial-dark text-[1.55rem] md:text-[1.75rem] leading-[1.3]">
            &ldquo;Toda emoción merece un espacio
            <span className="text-terracotta"> donde poder ser escuchada</span>.&rdquo;
            <span className="block mt-4 text-[0.72rem] tracking-[0.32em] uppercase not-italic text-accent-magenta">
              <span className="inline-block h-px w-8 bg-accent-magenta/50 align-middle mr-3" />
              Berenice
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}
